import React, { useState, useEffect } from "react";
import { Form, Row, Col, Card, Button, ListGroup, Badge, Spinner } from "react-bootstrap";
import AttachmentApi from "../../api/AttachmentApi";
import { FaTrash, FaDownload, FaFile } from "react-icons/fa";

export default function WorkflowAttachmentSelector({
  config,
  onChange,
  readOnly,
  eventType = "onUpdate", // Default to onUpdate if not provided
  workflowId,
  actionIndex,
  requiredMediaType,
}) {
  // If onCreate, force file source to be staticFile
  const defaultFileSource = eventType === "onCreate" ? "staticFile" : (config?.fileSource || "workflowObject");

  const [fileSource, setFileSource] = useState(defaultFileSource);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formatError, setFormatError] = useState("");

  const getMediaTypeDetails = (typeStr) => {
    const type = (typeStr || "").toUpperCase();
    if (type === "DOCUMENT") {
      return {
        label: "Document",
        accept: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv",
        extensions: ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv"],
        fileTypeOption: "Document",
        description: "PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV",
      };
    } else if (type === "VIDEO") {
      return {
        label: "Video",
        accept: "video/*,.mp4,.3gp,.mov,.avi,.mkv,.webm",
        extensions: ["mp4", "3gp", "mov", "avi", "mkv", "webm"],
        fileTypeOption: "Video",
        description: "MP4, 3GP, MOV, AVI, WEBM",
      };
    } else if (type === "IMAGE") {
      return {
        label: "Image",
        accept: "image/*,.jpg,.jpeg,.png,.gif,.webp",
        extensions: ["jpg", "jpeg", "png", "gif", "webp"],
        fileTypeOption: "Image",
        description: "JPG, JPEG, PNG, GIF, WEBP",
      };
    }
    return null;
  };

  const mediaDetails = getMediaTypeDetails(requiredMediaType);

  // Sync default fileType when requiredMediaType changes and fileSource is workflowObject
  useEffect(() => {
    if (mediaDetails && fileSource === "workflowObject") {
      if (config?.fileType !== mediaDetails.fileTypeOption) {
        onChange("fileType", mediaDetails.fileTypeOption);
      }
    }
  }, [requiredMediaType, fileSource]);

  // Automatically switch to staticFile when eventType changes to onCreate
  useEffect(() => {
    if (eventType === "onCreate" && fileSource !== "staticFile") {
      setFileSource("staticFile");
      onChange("fileSource", "staticFile");
    }
  }, [eventType]);

  // Load attachments when workflowId and actionIndex are available
  useEffect(() => {
    if (workflowId && typeof actionIndex === 'number' && fileSource === "staticFile") {
      loadAttachments();
    }
  }, [workflowId, actionIndex, fileSource]);

  // Helper function to extract value from field (handles both primitive and object formats)
  const getFieldValue = (field) => {
    if (field === null || field === undefined) return '';
    if (typeof field === 'object') {
      return field.value || field.id || field.label || '';
    }
    return field;
  };

  const loadAttachments = async () => {
    if (!workflowId || typeof actionIndex !== 'number') return;

    setLoading(true);
    try {
      const response = await AttachmentApi.getWorkflowAttachments(workflowId, actionIndex);
      if (response.success && response.data) {
        setUploadedFiles(response.data);
        // Store attachment IDs in config
        onChange("attachmentIds", response.data.map(f => getFieldValue(f.id)));
      }
    } catch (error) {
      console.error("Error loading attachments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSourceChange = (e) => {
    const source = e.target.value;
    setFileSource(source);
    onChange("fileSource", source);
  };

  const validateSelectedFiles = (files) => {
    if (!mediaDetails) return { valid: true };
    for (const file of files) {
      const fileName = file.name || "";
      const ext = fileName.split(".").pop().toLowerCase();
      const mimeType = (file.type || "").toLowerCase();

      const matchesExt = mediaDetails.extensions.includes(ext);
      const matchesMime = mimeType.startsWith(mediaDetails.label.toLowerCase()) || 
        (mediaDetails.label === "Document" && (mimeType.includes("pdf") || mimeType.includes("word") || mimeType.includes("document") || mimeType.includes("excel") || mimeType.includes("sheet") || mimeType.includes("text") || mimeType.includes("csv")));

      if (!matchesExt && !matchesMime) {
        return {
          valid: false,
          error: `Invalid format for "${fileName}". Template requires a ${mediaDetails.label} attachment (${mediaDetails.description}).`,
        };
      }
    }
    return { valid: true };
  };

  const handleStaticFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const validation = validateSelectedFiles(files);
    if (!validation.valid) {
      setFormatError(validation.error);
      e.target.value = "";
      return;
    }
    setFormatError("");

    if (files[0]?.name) {
      onChange("fileName", files[0].name);
    }

    if (!workflowId) {
      // If workflow not saved yet, store files temporarily
      onChange("staticFiles", files);
      return;
    }

    // Upload files immediately if workflow exists
    setUploading(true);
    try {
      // Clear pending staticFiles so uploadPendingAttachments will not re-upload on Save
      onChange("staticFiles", null);

      const response = await AttachmentApi.uploadWorkflowAttachments(
        workflowId,
        actionIndex,
        files,
        config?.description || ''
      );

      if (response.success && response.data) {
        // If single media template requirement (e.g. WhatsApp document header), cleanup old uploaded files
        if (mediaDetails && uploadedFiles.length > 0) {
          for (const oldFile of uploadedFiles) {
            const oldId = getFieldValue(oldFile.id);
            if (oldId) {
              try { await AttachmentApi.deleteAttachment(oldId); } catch (err) {}
            }
          }
        }

        const newFiles = mediaDetails ? response.data : [...uploadedFiles, ...response.data];
        setUploadedFiles(newFiles);
        onChange("attachmentIds", newFiles.map(f => getFieldValue(f.id)));
        if (response.data[0]?.file_name) {
          onChange("fileName", getFieldValue(response.data[0].file_name));
        }
        // Clear file input
        e.target.value = '';
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("Failed to upload files. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    const rawId = getFieldValue(fileId);
    if (!rawId) return;

    if (!window.confirm("Are you sure you want to delete this file?")) {
      return;
    }

    try {
      const response = await AttachmentApi.deleteAttachment(rawId);
      if (response.success) {
        const updatedFiles = uploadedFiles.filter(f => getFieldValue(f.id) !== rawId);
        setUploadedFiles(updatedFiles);
        onChange("attachmentIds", updatedFiles.map(f => getFieldValue(f.id)));
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      alert("Failed to delete file. Please try again.");
    }
  };

  const handleDownloadFile = async (fileId, fileName) => {
    const rawId = getFieldValue(fileId);
    if (!rawId) {
      alert("Invalid attachment file ID.");
      return;
    }

    try {
      const blob = await AttachmentApi.downloadAttachment(rawId);
      if (!blob || blob.size === 0) {
        alert('Download failed: received empty file.');
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Failed to download file. Please try again.");
    }
  };

  const handleFieldChange = (field, value) => {
    onChange(field, value);
  };

  const fileMatchingTypes = [
    "Equals",
    "Not Equals",
    "Contains",
    "Not Contains",
    "Starts With",
    "Ends With",
  ];

  const fileTypes = ["Document", "Image", "Video"];

  return (
    <Card className="mt-3">
      <Card.Header>Attachment Rule</Card.Header>
      <Card.Body>
        <Form.Group>
          <Form.Label>File Source</Form.Label>
          <Form.Select
            value={fileSource}
            onChange={handleSourceChange}
            disabled={readOnly || eventType === "onCreate"}
          >
            <option value="workflowObject">From Workflow Object</option>
            <option value="staticFile">Static File</option>
          </Form.Select>
          {eventType === "onCreate" && (
            <small className="text-muted">
              Only static files are available for onCreate events (no files exist on new records)
            </small>
          )}
        </Form.Group>

        {fileSource === "staticFile" && (
          <>
            <Form.Group className="mt-2">
              <Form.Label>Select File(s)</Form.Label>
              <Form.Control
                type="file"
                multiple={!mediaDetails}
                accept={mediaDetails ? mediaDetails.accept : undefined}
                onChange={handleStaticFileChange}
                disabled={readOnly || uploading}
                isInvalid={!!formatError}
              />
              {formatError && (
                <div className="alert alert-danger py-2 px-3 mt-2 mb-2" style={{ fontSize: "0.875rem" }}>
                  {formatError}
                </div>
              )}
              {uploading && (
                <div className="mt-2">
                  <Spinner size="sm" className="me-2" />
                  <span className="text-muted">Uploading files...</span>
                </div>
              )}
              {!workflowId && (
                <Form.Text className="text-muted">
                  Files will be uploaded when you save the workflow.
                </Form.Text>
              )}
            </Form.Group>

            {uploadedFiles.length > 0 && (
              <Form.Group className="mt-3">
                <Form.Label>Uploaded Files</Form.Label>
                {loading ? (
                  <div className="text-center py-3">
                    <Spinner size="sm" />
                    <span className="ms-2 text-muted">Loading files...</span>
                  </div>
                ) : (
                  <ListGroup>
                    {uploadedFiles.map((file) => (
                      <ListGroup.Item
                        key={file.id}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <div className="d-flex align-items-center">
                          <FaFile className="me-2 text-muted" />
                          <div>
                            <div>{getFieldValue(file.file_name)}</div>
                            {file.file_size && (() => {
                              const size = getFieldValue(file.file_size);
                              return size ? (
                                <small className="text-muted">
                                  {(Number(size) / 1024).toFixed(2)} KB
                                </small>
                              ) : null;
                            })()}
                          </div>
                        </div>
                        <div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => handleDownloadFile(file.id, getFieldValue(file.file_name))}
                            disabled={readOnly}
                          >
                            <FaDownload />
                          </Button>
                          {!readOnly && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteFile(file.id)}
                            >
                              <FaTrash />
                            </Button>
                          )}
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Form.Group>
            )}
          </>
        )}

        {fileSource === "workflowObject" && (
          <Row className="mt-2">
            <Col md={4}>
              <Form.Group>
                <Form.Label>File Matching Type</Form.Label>
                <Form.Select
                  value={config?.fileMatchingType || ""}
                  onChange={(e) =>
                    handleFieldChange("fileMatchingType", e.target.value)
                  }
                  required={!readOnly}
                  disabled={readOnly}
                >
                  <option value="">-Select Matching Type-</option>
                  {fileMatchingTypes?.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>File Type</Form.Label>
                <Form.Select
                  value={mediaDetails ? mediaDetails.fileTypeOption : (config?.fileType || "")}
                  onChange={(e) => {
                    if (mediaDetails) {
                      handleFieldChange("fileType", mediaDetails.fileTypeOption);
                    } else {
                      handleFieldChange("fileType", e.target.value);
                    }
                  }}
                  required={!readOnly}
                  disabled={readOnly || !!mediaDetails}
                >
                  {!mediaDetails && <option value="">-Select File Type-</option>}
                  {(mediaDetails ? [mediaDetails.fileTypeOption] : fileTypes).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
                {mediaDetails && (
                  <Form.Text className="text-muted">
                    Locked to <strong>{mediaDetails.fileTypeOption}</strong> as required by WhatsApp template header.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>File Name</Form.Label>
                <Form.Control
                  type="text"
                  value={config?.fileName || ""}
                  onChange={(e) =>
                    handleFieldChange("fileName", e.target.value)
                  }
                  placeholder="Enter file name"
                  disabled={readOnly}
                />
              </Form.Group>
            </Col>
          </Row>
        )}
      </Card.Body>
    </Card>
  );
}
